/**
 * Client TanStack Query — cache applicatif partagé par les trois espaces.
 *
 * Réglages pensés pour un back-office : deux ou trois personnes écrivent, les
 * données changent peu entre deux actions, mais **le plan de salle doit être
 * frais** (un siège vendu il y a dix secondes ne doit pas apparaître libre).
 * D’où un `staleTime` court par défaut et une invalidation explicite après
 * chaque mutation.
 */

import { QueryClient, type QueryKey } from '@tanstack/react-query'

import { ApiError, ErreurReseau } from './api'

/** Une erreur métier ne se retente jamais : la réponse serait identique. */
function doitRetenter(compteEchecs: number, erreur: unknown): boolean {
  if (compteEchecs >= 2) return false
  if (erreur instanceof ApiError) return erreur.http >= 500
  if (erreur instanceof ErreurReseau) return true
  return false
}

export function creerQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 15 s : assez pour éviter les rafales de requêtes en navigation,
        // assez court pour que le guichet voie l’état réel de la salle.
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        retry: doitRetenter,
        retryDelay: (tentative) => Math.min(1_000 * 2 ** tentative, 5_000),
        // Le personnel passe d’un onglet à l’autre toute la journée : refaire
        // toutes les requêtes à chaque retour de focus serait du bruit.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // Rejouer une mutation, c’est risquer un doublon de billet. Jamais.
        retry: false,
      },
    },
  })
}

/** Instance unique de l’application (les tests en créent une par cas). */
export const queryClient: QueryClient = creerQueryClient()

/**
 * Invalide plusieurs racines de clés d’un coup, après une mutation :
 *
 * ```ts
 * await invalider(['plan', representationId], ['ventes'], ['tableaux'])
 * ```
 */
export async function invalider(...cles: readonly QueryKey[]): Promise<void> {
  await Promise.all(
    cles.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  )
}

/**
 * Vide tout le cache — utilisé à la déconnexion : les données d’un utilisateur
 * ne doivent pas rester visibles pour le suivant sur le même poste.
 */
export function viderCache(): void {
  queryClient.clear()
}
